import { IdName } from 'src/app/shared/models/id-name';

export interface CompetenceRequirementMatrixExportQuery {
  excludedPositionIds: string[];
  excludedRoleIds: string[];
  excludedCompetenceIds: string[];
}
import { Observable } from 'rxjs/internal/Observable';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { getTenantId } from 'src/app/services/store-snapshot.service';
import { ApiResponse } from 'src/app/core/models/api-response';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { MainTableFilterModulesEnum } from 'src/app/core/enums/main-table-filter-modules.enum';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import { ICopyCompetenceRequirementModel } from '../model/competence-requirement-copy.model';
import { ICompetenceRequirementModel } from '../model/competence-requirement.model';

@Injectable({
  providedIn: 'root',
})
export class CompetenceRequirementsApiService {
  private get baseUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/Competence/CompetenceRequirement`;
  }

  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
    params: new HttpParams(),
  };

  constructor(private http: HttpClient) {}

  getCompetenceRequirements(
    currentFilter?: MainTableFilterModel
  ): Observable<ApiResponse<ICompetenceRequirementModel[]>> {
    const url = `${this.baseUrl}/`;
    // Table view: do not send departmentId - only matrix view uses department filter
    const filterForTable = currentFilter
      ? { ...currentFilter, departmentId: undefined }
      : currentFilter;
    const params = createFilterParams(filterForTable);

    return this.http.get<ApiResponse<ICompetenceRequirementModel[]>>(url, {
      ...this.httpOptions,
      params,
    });
  }

  getCompetenceRequirementsForMatrix(
    departmentId?: string
  ): Observable<ICompetenceRequirementModel[]> {
    const url = `${this.baseUrl}/${departmentId}/Matrix`;

    return this.http.get<ICompetenceRequirementModel[]>(url, {
      ...this.httpOptions,
    });
  }

  getAllCompetenceRequirements(): Observable<IdName[]> {
    const url = `${this.baseUrl}/List`;
    return this.http.get<IdName[]>(url, this.httpOptions);
  }

  addCompetenceRequirement(
    competenceRequirement: ICompetenceRequirementModel
  ): Observable<ICompetenceRequirementModel> {
    return this.http.post<ICompetenceRequirementModel>(
      this.baseUrl,
      competenceRequirement,
      this.httpOptions
    );
  }

  getCompetenceRequirementsById(id: string): Observable<ICompetenceRequirementModel> {
    const url = `${this.baseUrl}/${id}`;
    return this.http.get<ICompetenceRequirementModel>(url, this.httpOptions);
  }

  updateCompetenceRequirement(competenceRequirement: ICompetenceRequirementModel) {
    const url = `${this.baseUrl}/${competenceRequirement.id}`;
    return this.http.put<void>(url, competenceRequirement, this.httpOptions);
  }

  deleteCompetenceRequirement(id: string): Observable<void> {
    const url = `${this.baseUrl}/${id}`;
    return this.http.delete<void>(url, this.httpOptions);
  }

  copyCompetenceRequirement(copyCompetenceReq: ICopyCompetenceRequirementModel): Observable<void> {
    const url = `${this.baseUrl}/Copy`;
    return this.http.put<void>(url, copyCompetenceReq, this.httpOptions);
  }

  downloadCompetenceRequirementMatrixPdf(
    departmentId: string,
    query: CompetenceRequirementMatrixExportQuery
  ): Observable<Blob> {
    const url = `${this.baseUrl}/${departmentId}/Matrix/pdf`;
    return this.http.get(url, {
      headers: this.httpOptions.headers,
      params: this.buildMatrixExportParams(query),
      responseType: 'blob',
    });
  }

  downloadCompetenceRequirementMatrixExcel(
    departmentId: string,
    query: CompetenceRequirementMatrixExportQuery
  ): Observable<Blob> {
    const url = `${this.baseUrl}/${departmentId}/Matrix/excel`;
    return this.http.get(url, {
      headers: this.httpOptions.headers,
      params: this.buildMatrixExportParams(query),
      responseType: 'blob',
    });
  }

  private buildMatrixExportParams(query: CompetenceRequirementMatrixExportQuery): HttpParams {
    let params = new HttpParams();
    for (const id of query.excludedPositionIds) {
      params = params.append('ExcludedPositionIds', id);
    }
    for (const id of query.excludedRoleIds) {
      params = params.append('ExcludedRoleIds', id);
    }
    for (const id of query.excludedCompetenceIds) {
      params = params.append('ExcludedCompetenceIds', id);
    }
    return params;
  }
}
